import { useState } from "react";
import type { AiGenerationAdminInfo } from "@media-viewer/shared";

function optionsSummary(options: Record<string, unknown>): string {
  return ["aspectRatio", "resolution", "quality"]
    .map((k) => options[k])
    .filter((v): v is string => typeof v === "string" && v !== "")
    .join(" · ");
}

export function AdminGenerationPanel({ info }: { info: AiGenerationAdminInfo }) {
  const [copied, setCopied] = useState(false);
  const opts = optionsSummary(info.options);
  const by = info.generatedByName ?? (info.generatedByUserId != null ? `User #${info.generatedByUserId}` : "Deleted user");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(info.prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <section aria-label="AI generation details" className="rounded border border-amber-700 bg-zinc-900 p-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="text-base font-semibold text-zinc-100">AI generation</h3>
        <span className="rounded bg-amber-700/40 px-2 py-0.5 text-xs text-amber-100">admins only</span>
      </div>
      <dl className="grid grid-cols-[110px_minmax(0,1fr)] gap-x-3 gap-y-2 text-sm">
        <dt className="text-zinc-400">Generated by</dt>
        <dd className="break-words text-zinc-200">{by}</dd>
        <dt className="text-zinc-400">When</dt>
        <dd className="text-zinc-200">{new Date(info.createdAt).toLocaleString()}</dd>
        <dt className="text-zinc-400">Model</dt>
        <dd className="text-zinc-200">{info.modelName}{opts && ` · ${opts}`}</dd>
        <dt className="text-zinc-400">Cost</dt>
        <dd className="text-zinc-200">{info.credits} credits (${(info.credits / 100).toFixed(2)})</dd>
        <dt className="text-zinc-400">Prompt</dt>
        <dd className="whitespace-pre-line break-words text-zinc-200">{info.prompt}</dd>
        {info.negativePrompt && (
          <>
            <dt className="text-zinc-400">Negative prompt</dt>
            <dd className="whitespace-pre-line break-words text-zinc-200">{info.negativePrompt}</dd>
          </>
        )}
        <dt className="text-zinc-400">Example images</dt>
        <dd className="text-zinc-200">
          {info.exampleMediaIds.length === 0 && info.generatedExampleIds.length === 0 && info.deviceExampleCount === 0 ? (
            "None (prompt only)"
          ) : (
            <div className="flex flex-col gap-1">
              {info.exampleMediaIds.map((id) => (
                <a key={id} href={`/media/${id}`} className="text-sky-300 hover:text-sky-200">
                  Collection image #{id}
                </a>
              ))}
              {info.generatedExampleIds.length > 0 && (
                <span className="text-zinc-400">{info.generatedExampleIds.length} earlier results</span>
              )}
              {info.deviceExampleCount > 0 && (
                <span className="text-zinc-400">{info.deviceExampleCount} device images (not kept)</span>
              )}
            </div>
          )}
        </dd>
      </dl>
      <button
        onClick={copy}
        className="mt-3 min-h-10 w-full rounded border border-zinc-700 px-3 text-sm text-zinc-300 hover:bg-zinc-800"
      >
        {copied ? "Copied" : "Copy prompt"}
      </button>
    </section>
  );
}
